"use client";

// Client component que dispara UN pageview al montar la ruta.
// Se renderiza en el layout del portal para cubrir todas las pages,
// y toma slug/path/jobId como props así el layout puede setearlos.
//
// Sesiones: guardamos un session_id en localStorage con TTL de 30min de
// inactividad. Cada nueva visita después de esa ventana genera un session
// nuevo — heurística web tradicional para distinguir uniques vs pageviews.

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { recordPortalView } from "./_analytics-actions";

const LS_SESSION = "talenv_portal_session";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutos

type SessionBlob = { id: string; lastSeenAt: number };

function getOrCreateSession(): string {
  try {
    const raw = localStorage.getItem(LS_SESSION);
    const now = Date.now();
    if (raw) {
      const parsed = JSON.parse(raw) as SessionBlob;
      // Válida si el last-seen fue hace < TTL. Sino la reciclamos.
      if (parsed?.id && parsed?.lastSeenAt && now - parsed.lastSeenAt < SESSION_TTL_MS) {
        const next: SessionBlob = { id: parsed.id, lastSeenAt: now };
        localStorage.setItem(LS_SESSION, JSON.stringify(next));
        return parsed.id;
      }
    }
    const fresh: SessionBlob = {
      id: (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)) + "-" + now.toString(36),
      lastSeenAt: now,
    };
    localStorage.setItem(LS_SESSION, JSON.stringify(fresh));
    return fresh.id;
  } catch {
    // localStorage bloqueado (modo privado) — session efímera por request.
    return "no-storage";
  }
}

// Extrae el UUID del jobId del pathname del detalle del job.
// Match contra `/portal/<slug>/jobs/<uuid>` — solo el detalle mismo,
// no rutas hijas como `.../jobs/<uuid>/apply`.
function extractJobIdFromPath(path: string): string | null {
  const m = path.match(/\/portal\/[^/]+\/jobs\/([0-9a-f-]{36})\/?$/i);
  return m ? m[1] : null;
}

export function PortalAnalyticsTracker({ slug }: { slug: string }) {
  const pathname = usePathname();
  // Evita doble ping en dev por StrictMode (o si Next re-renderea el layout
  // sin cambio de ruta real). La ref trackea la última ruta pingeada.
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;
    if (lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;

    // No trackeamos las páginas transaccionales del portal — el análisis
    // se enfoca en "descubrimiento" (portal home y detalle de jobs).
    // apply y cv son formularios, y my-application es lookup — todos
    // fuera del funnel de descubrimiento.
    const isTransactional =
      pathname.includes("/apply") ||
      pathname.includes("/cv") ||
      pathname.includes("/my-application");
    if (isTransactional) return;

    const sessionId = getOrCreateSession();
    const jobId = extractJobIdFromPath(pathname);

    // Fire and forget — no bloquea nada. Cualquier error se traga en el
    // server action (return {ok:false}).
    void recordPortalView({
      slug,
      path: pathname,
      jobId,
      sessionId,
    });
  }, [pathname, slug]);

  return null;
}
